import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Button from '../../../../components/Button';
import ErrorMessage from '../../../../components/ErrorMessage';
import {
  RescuePlacementModel,
  RedirectToRescuePlacementHome,
  transferringOwnershipTitle,
  rescuePlacementMainQuery,
  AccountInformation,
} from '../rescuePlacementUtils';
import { gqlTypes } from '../../../../types';
import { gql, useMutation } from '@apollo/client';
import PetView from '../PetView/PetView';
import GoBackLink from '../components/GoBackLink/GoBackLink';
import EditCreditCardBillingInfo, {
  UpdateCreditCardBillingInfoFunction,
} from '../../../Checkout/pages/Billing/EditCreditCardBillingInfo';
import { UserFacingError, getCustomerMessageFromApolloError, logInternalError } from '../../../../lib/errors';
import Checkbox from '../../../../components/Checkbox';
import RecurlyProvider from '../../../../lib/RecurlyProvider';
import styles from './PetPermanentPlacement.module.scss';

const rescueTransferPetPermanentlyMutation = gql`
  mutation ECOMMERCE_rescueTransferPetPermanently($input: RescueTransferPetPermanentlyInput!) {
    rescueTransferPetPermanently(input: $input) {
      accountCreated
      newAccountTemporaryPassword
      billingAccount {
        billingInfo {
          firstName
          lastName
          address {
            line1
            line2
            city
            state
            zip
            country
          }
          paymentInfo {
            __typename
            ... on ObfuscatedCardInfo {
              lastFour
              type
              month
              year
            }
          }
        }
      }
    }
  }
`;

function PermanentPlacementSuccess({
  data,
  pet,
  email,
}: {
  data?: gqlTypes.ECOMMERCE_rescueTransferPetPermanently;
  pet: gqlTypes.petDetails;
  email: string;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <h2>{t('success')}</h2>
      <p>
        <Trans
          i18nKey="ownership_of_pet_name_was_transferred_to_email"
          values={{ petName: pet.name, email }}
          components={{ strong: <strong /> }}
        />
      </p>
      {data && data.rescueTransferPetPermanently && <AccountInformation data={data.rescueTransferPetPermanently} />}
    </div>
  );
}

interface NoBillingInfoFormProps {
  onSubmit(data: { firstName: string; lastName: string }): void;
  error?: string | null;
  submitting: boolean;
}

function NoBillingInfoForm({ onSubmit, submitting, error: errorFromProps }: NoBillingInfoFormProps) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const errorToShow = error || errorFromProps;

  const doSubmit = () => {
    if (!firstName || !lastName) {
      setError(t('please_enter_a_valid_first_and_last_name'));
      return;
    }
    onSubmit({ firstName, lastName });
  };

  return (
    <div className={styles.main}>
      <p>
        <strong>{t('important')}</strong> {t('if_you_dont_enter_billing_information_for_adopter')}
      </p>
      <p>{t('they_will_receive_email_with_gift_card_redemption_code')}</p>
      <div className={classNames(styles.basicForm, styles.wide)}>
        {errorToShow && (
          <div className={styles.textDanger}>
            <div>{errorToShow}</div>
          </div>
        )}
        <div className={styles.formRow}>
          <input
            type="text"
            placeholder={t('first_name')}
            value={firstName}
            onChange={(e) => {
              setError(null);
              setFirstName(e.currentTarget.value.trim());
            }}
          />
          <input
            type="text"
            placeholder={t('last_name')}
            value={lastName}
            onChange={(e) => {
              setError(null);
              setLastName(e.currentTarget.value.trim());
            }}
          />
        </div>
      </div>
      <div className={styles.actionContainer}>
        <Button disabled={submitting} type="submit" onClick={doSubmit}>
          {t('transfer')}
        </Button>
      </div>
    </div>
  );
}

async function validateGiftCardCode(code: string) {
  const provider = new RecurlyProvider();
  const pricing = provider.value.Pricing.Checkout();
  try {
    await pricing.giftCard(code);
    return true;
  } catch (err) {
    console.warn(err);
    return false;
  }
}

export default function PetPermanentPlacement({ model, petId }: { model: RescuePlacementModel; petId: string }) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [transferPetPermanently, { data }] = useMutation<
    gqlTypes.ECOMMERCE_rescueTransferPetPermanently,
    gqlTypes.ECOMMERCE_rescueTransferPetPermanentlyVariables
  >(rescueTransferPetPermanentlyMutation, {
    refetchQueries: [{ query: rescuePlacementMainQuery }],
  });

  // Note: after the pet is transferred the current user won't have access to it,
  // so after the refetch query runs it will be gone from the model. In order to
  // properly display a "success" message, keep the pet in React state that will
  // remain unchanged.
  const [pet] = useState(() => model.getPetById(petId));

  const [email, setEmail] = useState('');
  const [giftCardRedemptionCode, setGiftCardRedemptionCode] = useState('');
  const [complete, setComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [noBilling, setNoBilling] = useState(false);
  const [noBillingError, setNoBillingError] = useState<string | null>(null);

  const doTransfer = useCallback(
    async ({
      firstName,
      lastName,
      billingAccountInput,
    }: {
      firstName: string;
      lastName: string;
      billingAccountInput?: gqlTypes.BillingAccountInput;
    }) => {
      setSubmitting(true);
      try {
        await transferPetPermanently({
          variables: {
            input: {
              billingAccountInput,
              firstName,
              lastName,
              email,
              giftCardRedemptionCode,
              petId,
            },
          },
        });
      } finally {
        setSubmitting(false);
      }

      setComplete(true);
    },
    [email, giftCardRedemptionCode, petId, transferPetPermanently],
  );

  const noBillingSubmit = useCallback(
    ({ firstName, lastName }: { firstName: string; lastName: string }) => {
      if (!email) {
        setNoBillingError(t('please_enter_an_email_address_above'));
        return;
      }
      if (!giftCardRedemptionCode) {
        setNoBillingError(t('please_enter_a_gift_card_redemption_code_above'));
        return;
      }

      validateGiftCardCode(giftCardRedemptionCode)
        .then((valid) => {
          if (valid) {
            return doTransfer({ firstName, lastName }).catch((err) => {
              const customerMessage = getCustomerMessageFromApolloError(err);
              if (customerMessage) {
                setNoBillingError(customerMessage);
              } else {
                logInternalError(err);
                setNoBillingError(t('failed_to_process_adoption_transfer'));
              }
            });
          } else {
            throw new Error('Invalid gift card code');
          }
        })
        .catch(() => {
          setNoBillingError(t('that_gift_card_code_was_not_found'));
        });
    },
    [doTransfer, email, giftCardRedemptionCode, t],
  );

  const updateBillingInfo: UpdateCreditCardBillingInfoFunction = useCallback(
    async ({ input: billingAccountInput, firstName, lastName }) => {
      if (!email) {
        throw new UserFacingError(t('please_enter_an_email_address_above'));
      }
      if (!giftCardRedemptionCode) {
        throw new UserFacingError(t('please_enter_a_gift_card_redemption_code_above'));
      }
      await doTransfer({
        billingAccountInput,
        firstName,
        lastName,
      });
    },
    [doTransfer, email, giftCardRedemptionCode, t],
  );

  if (!pet) {
    return <RedirectToRescuePlacementHome />;
  }

  let body: JSX.Element;

  if (complete) {
    body = <PermanentPlacementSuccess data={data ?? undefined} email={email} pet={pet} />;
  } else {
    body = (
      <div className={styles.main}>
        <h2>{t('transfer_to_an_adopter')}</h2>
        <div>
          <div className={classNames(styles.basicForm, styles.wide)}>
            <h3>{t('email_address')}</h3>
            <p>{t('enter_the_email_address_of_the_user_you_wish_to_transfer')}</p>
            <input
              type="text"
              placeholder={t('email')}
              value={email}
              onChange={(e) => {
                setNoBillingError(null);
                setEmail(e.currentTarget.value.trim());
              }}
              data-hj-whitelist
            />
          </div>
        </div>
        <div>
          <div className={classNames(styles.basicForm, styles.wide)}>
            <h3>{t('gift_card_code')}</h3>
            <p>{t('enter_the_code_for_one_of_the_gift_cards_you_have_purchased')}</p>
            <input
              type="text"
              placeholder={t('gift_card_code_placeholder')}
              value={giftCardRedemptionCode}
              onChange={(e) => {
                setNoBillingError(null);
                setGiftCardRedemptionCode(e.currentTarget.value);
              }}
              data-hj-whitelist
            />
          </div>
        </div>
        <div>
          <div className={styles.wide}>
            <h3>{t('billing')}</h3>
            <Checkbox
              checked={!noBilling}
              onChange={(value) => setNoBilling(!value)}
              label={t('i_have_billing_information_for_the_adopter')}
            />
            {noBilling ? (
              <NoBillingInfoForm submitting={submitting} onSubmit={noBillingSubmit} error={noBillingError} />
            ) : (
              <>
                <p>{t('enter_the_billing_information_for_the_adopter')}</p>
                <p className={styles.rescuePlacementDisclaimer}>
                  {t('note_even_though_amount_gift_card_will_be_applied', { amount: 99 })}
                </p>
                <EditCreditCardBillingInfo
                  submitting={submitting}
                  setError={(errorMessage) => setError(errorMessage)}
                  clearError={() => setError(null)}
                  updateBillingInfo={updateBillingInfo}
                  actionText={t('transfer')}
                />
                {error && <ErrorMessage errors={[error]} />}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <GoBackLink />
      <PetView pet={pet} title={transferringOwnershipTitle(pet, t)} />
      {body}
    </div>
  );
}
